/**
 * React Query hooks for Finance > Invoices.
 *
 * - useInvoices(filters)        GET /v1/documents/invoices (offset paginated)
 * - useInvoiceFilters()         GET /v1/documents/invoices/filters
 * - useInvoiceDetail(id)        GET /v1/documents/invoices/{id}
 * - useEditInvoice()            PUT /v1/documents/invoices/{id}
 * - invoicePdfUrl(id)           GET /v1/documents/invoices/{id}/pdf (embedded viewer)
 *
 * Gated by documents.read (grid/detail) and documents.write (edit).
 * Mirrors useCoas.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from './api';
import { API_BASE } from './apiBase';
import type { AuditEvent } from './useOrders';

export const DOCUMENTS_API_BASE = `${API_BASE}/v1/documents/invoices`;

export type InvoiceStatus = 'extracted' | 'pending' | 'locked';

// Where the invoice is in the ERP push lifecycle (documents_invoices.sync_status).
// Null means not-yet-eligible. Only `ready_to_sync` rows can be synced. Once a
// row reaches `in_sage` / `in_bc` it is terminal. See COCKPIT.md section 4.5.
export type InvoiceSyncStatus = 'ready_to_sync' | 'sync_in_process' | 'in_sage' | 'in_bc';

// The ERP a Sync action targets. Matches the sync endpoint's `destination`.
export type SyncDestination = 'sage' | 'bc';

// The single combined Status the grid shows per row. It folds the document
// processing status and the ERP push lifecycle into one value a non-engineer
// can read at a glance. See COCKPIT.md section 4.5.
//   pending                 - not yet extracted from the source document.
//   not_in_erp              - extracted and queued, but not yet confirmed in an ERP.
//   in_erp                  - in an ERP; the paid/unpaid split is not yet available.
//   in_erp_unpaid           - in an ERP, not yet paid (added later by an ERP-presence check).
//   in_erp_partially_paid   - in an ERP, partially paid (added later by an ERP-presence check).
//   in_erp_paid             - in an ERP and fully paid (added later by an ERP-presence check).
//   in_ramp                 - present in Ramp (added later by the ERP-presence check).
// Only `pending` and `not_in_erp` are produced today (with `in_erp` as the
// interim folded value once a row is confirmed in Sage/BC). The paid variants
// and the Ramp value are defined now so the later ERP-presence split drops in
// cleanly.
export type CombinedInvoiceStatus =
  | 'pending'
  | 'not_in_erp'
  | 'in_erp'
  | 'in_erp_unpaid'
  | 'in_erp_partially_paid'
  | 'in_erp_paid'
  | 'in_ramp';

// ERP the matched vendor lives in: Sage 100, Business Central, or Ramp.
export type VendorErp = 'sage' | 'bc' | 'ramp';
// How the AI-extracted vendor name was matched to an ERP vendor record.
export type VendorMatch = 'exact' | 'fuzzy';

// Vendor-match fields appended by the api feat/invoices-vendor-match branch.
// They sit alongside the existing AI-extracted `vendor` name on both the row
// and the detail. `vendor_match_score` is the match confidence, 0-100.
export type VendorMatchFields = {
  matched_vendor_erp: VendorErp | null;
  matched_vendor_no: string | null;
  matched_vendor_name: string | null;
  vendor_match: VendorMatch | null;
  vendor_match_score: number | null;
};

// ERP-presence fields appended by the api ERP-presence check. They report
// whether a row that is already in an ERP has been paid, and the remaining
// balance. All optional: the api read path surfaces them only once the
// ERP-presence check is wired, so until then they are undefined and the
// combined status falls back to the folded `in_erp` value.
//   erp_payment_state - 'unpaid' | 'partially_paid' | 'paid' (Sage AP open/paid state).
//   erp_balance       - remaining amount owed.
//   erp_invoice_amt   - the original Sage invoice amount.
export type ErpPaymentState = 'unpaid' | 'partially_paid' | 'paid';
export type ErpPresenceFields = {
  erp_payment_state?: ErpPaymentState | null;
  erp_balance?: number | null;
  erp_invoice_amt?: number | null;
};

export type InvoiceRow = VendorMatchFields & ErpPresenceFields & {
  document_id: string;
  document_name: string;
  email_date: string | null;
  vendor: string | null;
  from_address: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  total_amount: number | null;
  currency: string | null;
  locked: number;
  status: string;
  extracted_at: string | null;
  location: string | null;
  gl_code?: string | null;
  // The GL account's readable description (e.g. 'Inventory-Ingredients
  // Purchases'), shown next to gl_code. Null when not resolved.
  gl_account_desc?: string | null;
  // How an auto-filled GL was chosen ('history_common' | 'history_recent' |
  // 'card'), and the % of the vendor's past invoices on that GL. Null when the
  // GL was set manually or not auto-filled.
  gl_source?: string | null;
  gl_confidence_pct?: number | null;
  // Set by the api once a GL code is saved on the detail page; locks the GL
  // field from further automated overwrites. Optional until the api read path
  // surfaces it.
  gl_locked?: number | boolean | null;
  // ERP push lifecycle. Optional: the api read handler only surfaces it once the
  // read path is extended to return it (the migration + sync handler already set
  // it). Until then it is undefined and the grid shows no sync pill.
  sync_status?: InvoiceSyncStatus | null;
  // Count of cockpit-only notes on the invoice (api list returns it as a
  // subquery COUNT). Drives the grid's has-notes indicator. Optional + defaulted
  // to 0 so the row degrades gracefully if the field is absent.
  notes_count?: number;
};

export type ListInvoicesResponse = {
  invoices: InvoiceRow[];
  total_count: number;
  limit: number;
  offset: number;
};

// Source filter value. Maps a matched-vendor ERP (sage/bc/ramp) or the
// "unmatched" bucket (no ERP vendor match) onto the api list endpoint's
// `source` param.
export type InvoiceSource = 'sage' | 'bc' | 'ramp' | 'unmatched';

export type InvoiceFilters = {
  q?: string;
  vendor?: string;
  status?: string;
  source?: InvoiceSource;
  invoice_date_from?: string;
  invoice_date_to?: string;
};

// Options for the Source filter dropdown, in the order shown.
export const SOURCE_FILTER_OPTIONS: { value: InvoiceSource; label: string }[] = [
  { value: 'sage', label: 'Sage' },
  { value: 'bc', label: 'MS BC' },
  { value: 'ramp', label: 'Ramp' },
  { value: 'unmatched', label: 'Unmatched' },
];

export type FilterOption = { value: string; label: string; sublabel?: string };
export type InvoiceFilterOptions = {
  statuses: FilterOption[];
  vendors: FilterOption[];
};

function buildQs(f: InvoiceFilters, offset: number): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.vendor) p.set('vendor', f.vendor);
  if (f.status) p.set('status', f.status);
  if (f.source) p.set('source', f.source);
  p.set('limit', '50');
  if (offset) p.set('offset', String(offset));
  return p.toString();
}

// Filter-only query string (no limit/offset), used by the Excel export so the
// download reflects the current filters and the full result set. Exported for
// unit testing (mirrors buildItemSalesQs in useItemSales.ts).
export function buildFilterQs(f: InvoiceFilters): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.vendor) p.set('vendor', f.vendor);
  if (f.status) p.set('status', f.status);
  if (f.source) p.set('source', f.source);
  if (f.invoice_date_from) p.set('invoice_date_from', f.invoice_date_from);
  if (f.invoice_date_to) p.set('invoice_date_to', f.invoice_date_to);
  return p.toString();
}

export function useInvoices(filters: InvoiceFilters = {}) {
  const query = useInfiniteQuery<ListInvoicesResponse, Error>({
    queryKey: ['documents-invoices', filters],
    queryFn: ({ pageParam }) =>
      apiGet<ListInvoicesResponse>(`${DOCUMENTS_API_BASE}?${buildQs(filters, (pageParam as number) ?? 0)}`),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.invoices.length, 0);
      return loaded < lastPage.total_count ? loaded : undefined;
    },
    staleTime: 15_000,
    retry: false,
  });
  const rows: InvoiceRow[] = (query.data?.pages ?? []).flatMap((p) => p.invoices);
  return {
    rows,
    totalCount: query.data?.pages[0]?.total_count ?? null,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
    refetch: () => query.refetch(),
  };
}

export function useInvoiceFilters() {
  const query = useQuery<InvoiceFilterOptions, Error>({
    queryKey: ['documents-invoices-filters'],
    queryFn: () => apiGet<InvoiceFilterOptions>(`${DOCUMENTS_API_BASE}/filters`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { options: query.data, isLoading: query.isLoading, error: query.error };
}

export type InvoiceLineItem = Record<string, unknown>;

export type InvoiceDetail = VendorMatchFields & ErpPresenceFields & {
  document_id: string;
  department: string;
  document_type: string;
  email_date: string | null;
  mailbox: string | null;
  location: string | null;
  document_name: string;
  graph_message_id: string;
  sharepoint_item_id: string | null;
  sharepoint_drive_id: string | null;
  created_at: string | null;
  status: string;
  locked: number;
  has_invoice_detail: number;
  vendor: string | null;
  from_address: string | null;
  not_invoice: boolean;
  not_invoice_reason: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  total_amount: number | null;
  currency: string | null;
  subtotal: number | null;
  tax: number | null;
  po_number: string | null;
  account_number?: string | null;
  bill_to?: string | null;
  line_items: unknown;
  // Which ERPs the matched vendor exists in. When length > 1 the detail page
  // shows the ERP switcher. erp_locked is set after a user switches the ERP.
  erp_options?: VendorErp[];
  erp_locked?: number | boolean | null;
  extracted_at: string | null;
  extraction_model: string | null;
  last_edited_by: string | null;
  last_edited_at: string | null;
  gl_code?: string | null;
  // The GL account's readable description, shown next to gl_code. See the grid
  // row type above.
  gl_account_desc?: string | null;
  // How an auto-filled GL was chosen + the vendor's consistency %. Null when set
  // manually or not auto-filled. See the grid row type above.
  gl_source?: string | null;
  gl_confidence_pct?: number | null;
  // Set true by the api after a GL code is saved; the GL field then shows as
  // locked. Optional until the api read path surfaces it.
  gl_locked?: number | boolean | null;
  // ERP push lifecycle + the single Sage-synced Comment. Both optional: the api
  // read handler surfaces them once its read path is extended (the migration and
  // sync/write handlers already write them). The Comment field pre-populates from
  // `comment` when present and saves via PUT regardless.
  sync_status?: InvoiceSyncStatus | null;
  comment?: string | null;
};

export function useInvoiceDetail(documentId: string | null | undefined) {
  return useQuery<InvoiceDetail, Error>({
    queryKey: ['documents-invoice', documentId],
    queryFn: () => apiGet<InvoiceDetail>(`${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId ?? '')}`),
    enabled: !!documentId,
    retry: false,
  });
}

// A vendor record looked up from its ERP by number. Returned by
// GET /v1/documents/invoices/vendor?erp={sage|bc}&no={vendorNo}. Read-only.
export type VendorRecord = {
  erp: VendorErp;
  vendor_no: string;
  vendor_name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postal: string | null;
  country: string | null;
  email: string | null;
  phone: string | null;
  terms: string | null;
  account: string | null;
  status: string | null;
};

// Fetches the canonical ERP vendor record for a matched invoice vendor. Only
// runs when both the ERP and the vendor number are set (i.e. the invoice
// actually matched a vendor); otherwise the query stays disabled.
export function useVendorRecord(
  erp: VendorErp | null | undefined,
  no: string | null | undefined,
) {
  return useQuery<VendorRecord, Error>({
    queryKey: ['documents-invoice-vendor', erp, no],
    queryFn: () =>
      apiGet<VendorRecord>(
        `${DOCUMENTS_API_BASE}/vendor?erp=${encodeURIComponent(erp ?? '')}&no=${encodeURIComponent(no ?? '')}`,
      ),
    enabled: !!erp && !!no,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export type EditInvoiceBody = {
  vendor?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  total_amount?: number | null;
  currency?: string | null;
  subtotal?: number | null;
  tax?: number | null;
  po_number?: string | null;
  line_items?: unknown;
  // The single Sage-synced Comment (AP_OpenInvoice.Comment is VARCHAR(40); the
  // api enforces the 40-char cap on write, the UI enforces it on input).
  comment?: string | null;
  // The GL code. Saving it locks the GL field (the api sets gl_locked) so the
  // automated extractor will not overwrite the manual value.
  gl_code?: string | null;
};

export function useEditInvoice() {
  const qc = useQueryClient();
  return useMutation<
    { document_id: string; updated: boolean },
    Error,
    { documentId: string; body: EditInvoiceBody }
  >({
    mutationFn: ({ documentId, body }) =>
      apiPut(`${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId)}`, body),
    onSuccess: (_d, { documentId }) => {
      void qc.invalidateQueries({ queryKey: ['documents-invoices'] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice', documentId] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice-audit', documentId] });
    },
  });
}

// Switch an invoice's ERP destination (for a vendor that exists in more than one
// ERP). PUT /v1/documents/invoices/{id}/erp. The api re-resolves the vendor in
// the target ERP, locks the row, clears the stale GL, and records the
// account-number rule when the invoice has an account number. `learned` is true
// when that rule was recorded.
export function useSetInvoiceErp() {
  const qc = useQueryClient();
  return useMutation<
    { document_id: string; matched_vendor_erp: string; matched_vendor_no: string | null; learned: boolean },
    Error,
    { documentId: string; erp: SyncDestination }
  >({
    mutationFn: ({ documentId, erp }) =>
      apiPut(`${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId)}/erp`, { erp }),
    onSuccess: (_d, { documentId }) => {
      void qc.invalidateQueries({ queryKey: ['documents-invoices'] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice', documentId] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice-audit', documentId] });
    },
  });
}

// Largest PDF the api accepts. Mirrors MAX_UPLOAD_BYTES in the api's
// tools/documents/src/documents-invoices-upload.ts: the file travels as base64
// inside a JSON body, so the raw file is capped at 7MB to keep the encoded body
// under the HTTP API's 10MB request limit.
export const INVOICE_UPLOAD_MAX_BYTES = 7 * 1024 * 1024;

export type InvoiceUploadBody = { filename: string; content_base64: string };

// Reads a File into the body POST /v1/documents/invoices/upload expects. The api
// only parses JSON, so the bytes go as base64 text (no `data:` prefix).
export async function buildInvoiceUploadBody(file: File): Promise<InvoiceUploadBody> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  // Chunked so a multi-MB PDF does not overflow the argument limit of fromCharCode.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return { filename: file.name, content_base64: btoa(binary) };
}

// Sends one file to the upload endpoint as JSON { filename, content_base64 } (the
// api does not parse multipart). Refuses a file over the api's 7MB cap up front.
export async function uploadInvoiceFile(file: File): Promise<{ document_id: string; document_name: string }> {
  if (file.size > INVOICE_UPLOAD_MAX_BYTES) {
    throw new Error(
      `${file.name} is larger than the ${Math.floor(INVOICE_UPLOAD_MAX_BYTES / (1024 * 1024))}MB upload limit`,
    );
  }
  return apiPost(`${DOCUMENTS_API_BASE}/upload`, await buildInvoiceUploadBody(file));
}

// Uploads a file directly as a new invoice, for cases that never arrive through
// the watched-mailbox email intake. The api stores the document under the same
// pipeline an emailed invoice uses (parsed by the same extractor), with `mailbox`
// set server-side to `Uploaded by {user}` rather than a real mailbox name. On
// success invalidates the list so the new row appears.
export function useUploadInvoice() {
  const qc = useQueryClient();
  return useMutation<{ document_id: string; document_name: string }, Error, File>({
    mutationFn: uploadInvoiceFile,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents-invoices'] });
    },
  });
}

// --- Notes ------------------------------------------------------------------
//
// Cockpit-only, multi-entry notes on an invoice, each with an author and a
// timestamp. Distinct from the single 40-char Sage-synced Comment.
//   GET  /v1/documents/invoices/{id}/notes  -> { data: InvoiceNote[] } (newest first)
//   POST /v1/documents/invoices/{id}/notes  with { body } -> { id }
// The api shapes (data envelope, `id`/`created_by` field names) are the source
// of truth; these types match documents-invoices-read.ts / -write.ts.

export type InvoiceNote = {
  id: number | string;
  document_id: string;
  body: string;
  created_by: string | null;
  created_at: string;
};

// Lists notes for an invoice, newest first. Adding a note also bumps the row's
// notes_count, so a successful add invalidates the list/detail queries (see
// useAddInvoiceNote). `enabled` lets a caller defer the load until a popover
// opens.
export function useInvoiceNotes(documentId: string | null | undefined, enabled = true) {
  return useQuery<{ data: InvoiceNote[] }, Error>({
    queryKey: ['documents-invoice-notes', documentId],
    queryFn: () =>
      apiGet<{ data: InvoiceNote[] }>(
        `${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId ?? '')}/notes`,
      ),
    enabled: !!documentId && enabled,
    retry: false,
  });
}

export function useAddInvoiceNote() {
  const qc = useQueryClient();
  return useMutation<{ id: number | string }, Error, { documentId: string; body: string }>({
    mutationFn: ({ documentId, body }) =>
      apiPost(`${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId)}/notes`, { body }),
    onSuccess: (_data, { documentId }) => {
      void qc.invalidateQueries({ queryKey: ['documents-invoice-notes', documentId] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice-audit', documentId] });
      // The list/detail carry notes_count; refresh so the has-notes indicator updates.
      void qc.invalidateQueries({ queryKey: ['documents-invoices'] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice', documentId] });
    },
  });
}

// --- Audit ------------------------------------------------------------------
//
// Cross-cutting audit_log feed for one invoice, populated by api-side handlers
// on edit / ERP switch / dismiss / restore / note-added (api #280 + #283). The
// event shape matches the commerce order audit read, so the `AuditEvent` type
// from useOrders is reused directly.
//   GET /v1/documents/invoices/{id}/audit  -> { events: AuditEvent[] }
// Gated by documents.read. `enabled` lets a caller defer the load until the
// notes modal opens (mirrors useOrderAudit).

export function useInvoiceAudit(documentId: string | null | undefined, enabled = true) {
  return useQuery<{ events: AuditEvent[] }, Error>({
    queryKey: ['documents-invoice-audit', documentId],
    queryFn: () =>
      apiGet<{ events: AuditEvent[] }>(
        `${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId ?? '')}/audit`,
      ),
    enabled: !!documentId && enabled,
    retry: false,
  });
}

// Body for POST /v1/documents/invoices/sync. One click covers N invoices.
export type SyncInvoicesBody = {
  destination: SyncDestination;
  invoice_ids: string[];
  posting_date: string; // YYYY-MM-DD
};

// Sage success: a generated CSV in S3 plus a presigned download link.
export type SyncSageResult = {
  push_id: string;
  destination: 'sage';
  csv_url: string;
  csv_key: string;
  count: number;
  invoice_ids: string[];
};

// BC, when disabled server-side (INVOICE_BC_SYNC_ENABLED off): the call records
// the click and returns this without writing to BC.
export type SyncBcDisabledResult = {
  push_id: string;
  destination: 'bc';
  status: 'disabled';
};

// BC, when enabled: one draft purchaseInvoice was created per covered invoice.
// erp_ref carries each created BC invoice id (also stored on the cockpit row).
export type SyncBcOkResult = {
  push_id: string;
  destination: 'bc';
  count: number;
  invoice_ids: string[];
  erp_ref: string[];
};

export type SyncInvoicesResult = SyncSageResult | SyncBcOkResult | SyncBcDisabledResult;

// POST /v1/documents/invoices/sync. Gated by documents.invoices.push. On success
// the synced rows advance to sync_in_process, so the list and detail are
// invalidated. A selection with no destination-eligible rows returns 422.
export function useSyncInvoices() {
  const qc = useQueryClient();
  return useMutation<SyncInvoicesResult, Error, SyncInvoicesBody>({
    mutationFn: (body) => apiPost<SyncInvoicesResult>(`${DOCUMENTS_API_BASE}/sync`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents-invoices'] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice'] });
    },
  });
}

// Marks a document as not-an-invoice (inactivates it). The server then hides it
// from the list, so we invalidate the list and the detail query on success.
export function useMarkNotInvoice() {
  const qc = useQueryClient();
  return useMutation<
    { document_id: string; not_invoice: boolean },
    Error,
    { documentId: string }
  >({
    mutationFn: ({ documentId }) =>
      apiPost(`${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId)}/not-invoice`, {}),
    onSuccess: (_d, { documentId }) => {
      void qc.invalidateQueries({ queryKey: ['documents-invoices'] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice', documentId] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice-audit', documentId] });
    },
  });
}

// Restores a document previously marked not-an-invoice.
export function useRestoreInvoice() {
  const qc = useQueryClient();
  return useMutation<
    { document_id: string; not_invoice: boolean },
    Error,
    { documentId: string }
  >({
    mutationFn: ({ documentId }) =>
      apiPost(`${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId)}/restore`, {}),
    onSuccess: (_d, { documentId }) => {
      void qc.invalidateQueries({ queryKey: ['documents-invoices'] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice', documentId] });
      void qc.invalidateQueries({ queryKey: ['documents-invoice-audit', documentId] });
    },
  });
}

// Direct URL for the embedded PDF viewer. The detail page fetches it as a blob
// to handle auth/errors. Cross-subdomain cookie is same-site, so credentials
// flow on the iframe GET.
export function invoicePdfUrl(documentId: string): string {
  return `${DOCUMENTS_API_BASE}/${encodeURIComponent(documentId)}/pdf`;
}

// Downloads the Invoices grid as an .xlsx using the current filters (q, vendor,
// status, source, invoice_date_from, invoice_date_to). Fetches with
// credentials so the cross-subdomain cookie flows, then triggers a browser
// download from the returned blob. Mirrors downloadCoasXlsx. Filename comes
// from the Content-Disposition header, falling back to invoices.xlsx.
export async function downloadInvoicesXlsx(filters: InvoiceFilters): Promise<void> {
  await downloadInvoicesFile(buildFilterQs(filters), 'export', 'invoices.xlsx');
}

// Downloads the invoice PDFs as a single .zip, so Accounting can grab every PDF
// in one file instead of opening each invoice individually (see COCKPIT.md 4.5
// for the Invoices grid). When one or more rows are selected, scopes the zip to
// just those document ids (api #658's `document_ids` param) instead of the grid
// filters, since a selection can exceed the filter-based export's row cap. With
// nothing selected, falls back to the same filter set as the Excel export.
export async function downloadInvoicesPdfZip(
  filters: InvoiceFilters,
  documentIds: string[] = [],
): Promise<void> {
  await downloadInvoicesFile(buildPdfZipQs(filters, documentIds), 'export-zip', 'invoices.zip');
}

// Query string for the PDF zip export: `document_ids=<comma-joined ids>` when
// one or more rows are selected (ignoring the filters entirely), else the same
// filter query string the Excel export uses. Exported for unit testing.
export function buildPdfZipQs(filters: InvoiceFilters, documentIds: string[]): string {
  if (documentIds.length > 0) {
    const p = new URLSearchParams();
    p.set('document_ids', documentIds.join(','));
    return p.toString();
  }
  return buildFilterQs(filters);
}

// Extracts a plain-text message from an api error response so a banner never
// shows a raw JSON body. The api error shape is `{"error":{"message": "..."}}`;
// falls back to the raw body, then a generic message, when that shape isn't
// present. Exported for unit testing.
export function extractErrorMessage(body: string, status: number): string {
  try {
    const parsed = JSON.parse(body) as { error?: { message?: unknown } };
    const message = parsed?.error?.message;
    if (typeof message === 'string' && message) return message;
  } catch {
    // Not JSON; fall through to the raw body.
  }
  return body || `Export failed (${status})`;
}

async function downloadInvoicesFile(
  qs: string,
  endpoint: 'export' | 'export-zip',
  fallbackFilename: string,
): Promise<void> {
  const res = await fetch(`${DOCUMENTS_API_BASE}/${endpoint}${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(extractErrorMessage(text, res.status));
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? fallbackFilename;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// Short display tag for the ERP a matched vendor lives in.
export function vendorErpLabel(erp: VendorErp | null | undefined): string {
  switch (erp) {
    case 'sage': return 'Sage';
    case 'bc': return 'MS BC';
    case 'ramp': return 'Ramp';
    default: return '';
  }
}

export function statusLabel(s: string): string {
  switch (s) {
    case 'extracted': return 'Extracted';
    case 'pending': return 'Pending';
    case 'locked': return 'Locked';
    default: return s;
  }
}

// Title Case label for the ERP push lifecycle (documents_invoices.sync_status).
export function syncStatusLabel(s: InvoiceSyncStatus): string {
  switch (s) {
    case 'ready_to_sync': return 'Ready to Sync';
    case 'sync_in_process': return 'Sync in process';
    case 'in_sage': return 'In Sage';
    case 'in_bc': return 'In BC';
  }
}

// Tone (background + text classes) per sync status, layered over the shared
// Pill base. Mirrors the Commerce Orders Status tone map.
export const SYNC_STATUS_TONES: Record<InvoiceSyncStatus, string> = {
  ready_to_sync: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  sync_in_process: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  in_sage: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  in_bc: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

// Derive the single combined Status shown in the grid from a row's processing
// status, ERP push lifecycle, matched ERP, and ERP-presence payment state.
// Rule (COCKPIT.md 4.5):
//   not yet extracted                              -> pending
//   matched_vendor_erp is ramp                     -> in_ramp (terminal)
//   sync_status is in_sage / in_bc:
//     erp_payment_state unpaid           -> in_erp_unpaid
//     erp_payment_state partially_paid   -> in_erp_partially_paid
//     erp_payment_state paid             -> in_erp_paid
//     null / undefined                   -> in_erp (fallback before the split)
//   otherwise (extracted / ready_to_sync / sync_in_process) -> not_in_erp
// The input only needs these driving fields, so both the row and the detail
// shape satisfy it. Defensive on a missing/unknown status (treats it as
// extracted, i.e. not_in_erp), so the UI degrades gracefully.
export function combinedInvoiceStatus(row: {
  status?: string | null;
  sync_status?: InvoiceSyncStatus | null;
  matched_vendor_erp?: VendorErp | null;
  erp_payment_state?: ErpPaymentState | null;
}): CombinedInvoiceStatus {
  if (row.status === 'pending') return 'pending';
  if (row.matched_vendor_erp === 'ramp') return 'in_ramp';
  if (row.sync_status === 'in_sage' || row.sync_status === 'in_bc') {
    switch (row.erp_payment_state) {
      case 'unpaid': return 'in_erp_unpaid';
      case 'partially_paid': return 'in_erp_partially_paid';
      case 'paid': return 'in_erp_paid';
      default: return 'in_erp';
    }
  }
  return 'not_in_erp';
}

// Title Case label for every combined status. Includes the paid variants and
// the Ramp value so the later ERP-presence split needs no new label wiring.
export function combinedStatusLabel(s: CombinedInvoiceStatus): string {
  switch (s) {
    case 'pending': return 'Pending';
    case 'not_in_erp': return 'Not in ERP';
    case 'in_erp': return 'In ERP';
    case 'in_erp_unpaid': return 'In ERP - Unpaid';
    case 'in_erp_partially_paid': return 'In ERP - Partially Paid';
    case 'in_erp_paid': return 'In ERP - Paid';
    case 'in_ramp': return 'In Ramp';
  }
}

// Tone (background + text classes) per combined status, layered over the shared
// Pill base. Mirrors the Commerce Orders Status tone map.
export const COMBINED_STATUS_TONES: Record<CombinedInvoiceStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  not_in_erp: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  // In ERP (paid/unpaid split not yet known) and In Ramp read neutral.
  in_erp: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  in_ramp: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  // Owed money reads as attention: unpaid is the strongest (red), partially
  // paid is amber.
  in_erp_unpaid: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  in_erp_partially_paid: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  // Fully paid is positive (green).
  in_erp_paid: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

// The combined-status options for the grid's Status filter, in display order.
// The backend now supplies the ERP-presence split, so the paid variants and
// In Ramp are offered alongside the folded In ERP fallback.
export const COMBINED_STATUS_FILTER_OPTIONS: { value: CombinedInvoiceStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'not_in_erp', label: 'Not in ERP' },
  { value: 'in_erp', label: 'In ERP' },
  { value: 'in_erp_unpaid', label: 'In ERP - Unpaid' },
  { value: 'in_erp_partially_paid', label: 'In ERP - Partially Paid' },
  { value: 'in_erp_paid', label: 'In ERP - Paid' },
  { value: 'in_ramp', label: 'In Ramp' },
];

// Filter rows to those whose invoice_date falls within [from, to] (inclusive,
// ISO strings). Rows with a null invoice_date are excluded when either bound
// is set. Mirrors the client-side status filter already applied in InvoicesList.
export function filterByInvoiceDateRange(
  rows: InvoiceRow[],
  from: string | undefined,
  to: string | undefined,
): InvoiceRow[] {
  if (!from && !to) return rows;
  return rows.filter((r) => {
    if (!r.invoice_date) return false;
    if (from && r.invoice_date < from) return false;
    if (to && r.invoice_date > to) return false;
    return true;
  });
}

// Format a total in the invoice's own currency, defaulting to USD when the
// extractor did not capture a currency. Mirrors useOrders.formatMoney but
// honors the per-row currency the invoice carries.
export function formatInvoiceTotal(
  v: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  const cur = currency && /^[A-Za-z]{3}$/.test(currency) ? currency.toUpperCase() : 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(n);
  } catch {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  }
}

// Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09):
// Invoices is one tool, one accessSlug (finance.invoices), no separate
// read/write/push levels — holding it grants all three, including the Sync to
// Sage / Sync to BC actions. Replaces the old documents.read / .write /
// .invoices.push trio.
export function hasDocumentsRead(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('finance.invoices');
}
export function hasDocumentsWrite(perms: string[] | undefined, superAdmin = false): boolean {
  return hasDocumentsRead(perms, superAdmin);
}
export function hasDocumentsPush(perms: string[] | undefined, superAdmin = false): boolean {
  return hasDocumentsRead(perms, superAdmin);
}
